/**
 * TemplateBlockHeaderPure
 *
 * Header for a template delay block. Shows the block's wait length as a
 * pill (e.g. "wait 3 days"), with a click affordance to open the inline
 * delay editor. The first block in a template is the "Day 0" entry —
 * for that case the host can either pass `days = 0` (renders "no wait
 * — first step") or hide the header entirely.
 *
 * Mirrors `CampaignDayHeaderPure` in tone and layout, but the right
 * slot is reserved for an optional remove button on chained
 * `wait_for_connect` blocks (templates have no per-contact "today" /
 * "see in template" affordances).
 */
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../cn';
import { weekDelayLabel } from '../CampaignDayCard/weekDelayLabel';

export type TemplateBlockHeaderPureProps = ComponentProps<'div'> & {
  /** Wait length for this block in whole days. `0` reads as "no wait". */
  days: number;
  /** When true, the wait pill reads "next week" / "the week after next" / "in N weeks". */
  alignWeek?: boolean;
  /**
   * Block index within the template (0-based). Surfaced as a faint
   * "Day N" badge on the left so the user can scan a long template
   * and refer to a specific block by number.
   */
  blockIndex?: number;
  /**
   * When set, renders a wait pill alongside the day label. Clicking
   * the pill fires `onEditDelay`, swapping in the inline editor.
   */
  onEditDelay?: () => void;
  /**
   * When true, the block is a `wait_for_connect` node. The header
   * swaps the "wait N days" pill for an "Wait for LinkedIn connection"
   * label so the user understands the block branches on a connection
   * acceptance event, not on a fixed timer. The block delay is the
   * MAX time to wait before falling through to the No branch.
   */
  isWaitForConnect?: boolean;
  /**
   * Optional content rendered on the right side of the header (e.g. a
   * delete button for the block, drag handle).
   */
  rightSlot?: ReactNode;
};

export function TemplateBlockHeaderPure({
  days,
  alignWeek,
  blockIndex,
  onEditDelay,
  isWaitForConnect,
  rightSlot,
  className,
  ...props
}: TemplateBlockHeaderPureProps) {
  const dayLabel = days === 1 ? 'day' : 'days';

  return (
    <div
      className={cn('flex items-baseline justify-between gap-2', className)}
      data-testid="template-block-header"
      data-wait-for-connect={isWaitForConnect ? 'true' : 'false'}
      {...props}>
      <div className="flex min-w-0 items-baseline gap-2">
        {blockIndex != null && (
          <span
            data-testid="template-block-header-index"
            className="text-xs font-semibold tracking-tight text-lynch-700 dark:text-lynch-300">
            Day {blockIndex + 1}
          </span>
        )}
        {isWaitForConnect && (
          <span
            data-testid="template-block-header-wait-for-connect"
            className="inline-flex items-center rounded-full border border-astronaut-200 bg-astronaut-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-astronaut-700 dark:border-astronaut-800 dark:bg-astronaut-950/50 dark:text-astronaut-200">
            Wait for connection
          </span>
        )}
        {!isWaitForConnect && onEditDelay != null && (
          <button
            type="button"
            data-testid="template-block-header-wait"
            data-days={days}
            onClick={onEditDelay}
            className={cn(
              // Same chrome as the campaign-side wait pill so a long
              // page that contains both reads as one design language.
              'group inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors',
              'border-lynch-200 bg-white text-lynch-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700',
              'dark:border-slate-700 dark:bg-slate-900 dark:text-lynch-200 dark:hover:border-blue-500 dark:hover:bg-blue-950 dark:hover:text-blue-300',
            )}>
            {days === 0 && <span data-testid="template-block-header-no-wait">no wait</span>}
            {days !== 0 && alignWeek && (
              <span data-testid="template-block-header-days" className="font-semibold">
                {weekDelayLabel(Math.max(1, Math.round(days / 7)))}
              </span>
            )}
            {days !== 0 && !alignWeek && (
              <>
                wait{' '}
                <span data-testid="template-block-header-days" className="font-semibold">
                  {days} {dayLabel}
                </span>
              </>
            )}
          </button>
        )}
        {/* No static fallback: when neither `isWaitForConnect` nor
            `onEditDelay` is set, the wait info is rendered by the
            host on the connector band ABOVE this card (matches the
            contact detail page's `CampaignDayDelayPure` chrome).
            Rendering it here too would double up. */}
      </div>
      {rightSlot}
    </div>
  );
}
